import { FormInput, FormSelect } from "@/components";
import useFilter from "./useFilter";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  YEAR_OF_COLLABORATION,
  GEO_REGION_ID,
  SCHOOL_NAME,
  ACCOUNT_TYPE,
  SCHOOL_TIER,
  STATE,
  CITY,
  SKU,
  NO_OF_WORK_STATIONS,
  NO_OF_FACULTY_DAYS,
} from "../consts";
import { X } from "lucide-react";
import { FC } from "react";
import { FilterProps } from "./types";
import {
  useTaskCategoryQuery,
  useTaskTermQuery,
  useGeoRegionsQuery,
} from "@/services/masterData";
import { useGetSchoolsQuery } from "@/services";
import { yearOptions } from "@/lib/utils";
import {
  accounttypeoptions,
  tierOptions,
  skuOptions
} from "@/pages/SalesOps/School/Add/TabsData/SchoolInfo/consts";
import { stateList } from "@/consts";

const Filter: FC<FilterProps> = ({
  onCancel,
  filterData,
  setFilterData,
  onApply,
}) => {
  const { form, onSubmit, requiredFields, isMobile, onClear } = useFilter({
    filterData,
    setFilterData,
    onCancel,
    onApply,
  });
  const { errors } = form.formState;

  const { data: geoRes, isLoading: isLoadingTerms } = useGeoRegionsQuery({});
  const { data: schoolRes, isLoading: isLoadingSchool } = useGetSchoolsQuery(
    {}
  );

  const schoolOptions =
    schoolRes?.data?.[0]?.records?.map((item: any) => ({
      label: item.schoolName,
      value: item.schoolId,
    })) || [];

  const regions =
    geoRes?.data?.[0]?.records?.map((item: any) => ({
      label: item.name,
      value: item._id,
    })) || [];

  return (
    <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">Filter</DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 gap-[25px]">
                {/* New Fields */}
                <FormSelect
                  name={YEAR_OF_COLLABORATION}
                  control={form.control}
                  label="Academic Collaboration"
                  placeholder="Enter year"
                  type="number"
                  error={errors[YEAR_OF_COLLABORATION]?.message}
                  isRequired={requiredFields.includes(YEAR_OF_COLLABORATION)}
                  options={yearOptions()}
                />

                <FormSelect
                  name={GEO_REGION_ID}
                  control={form.control}
                  label="Geo Region"
                  placeholder="Enter Geo Region"
                  error={errors[GEO_REGION_ID]?.message}
                  isRequired={requiredFields.includes(GEO_REGION_ID)}
                  options={regions}
                />

                <FormSelect
                  name={SCHOOL_NAME}
                  control={form.control}
                  label="School Name"
                  placeholder="Enter school name"
                  error={errors[SCHOOL_NAME]?.message}
                  isRequired={requiredFields.includes(SCHOOL_NAME)}
                  options={schoolOptions}
                />

                <FormSelect
                  name={ACCOUNT_TYPE}
                  control={form.control}
                  label="Account Type"
                  placeholder="Enter account type"
                  error={errors[ACCOUNT_TYPE]?.message}
                  isRequired={requiredFields.includes(ACCOUNT_TYPE)}
                  options={accounttypeoptions}
                />

                <FormSelect
                  name={SCHOOL_TIER}
                  control={form.control}
                  label="School Tier"
                  placeholder="Enter school tier"
                  error={errors[SCHOOL_TIER]?.message}
                  isRequired={requiredFields.includes(SCHOOL_TIER)}
                  options={tierOptions}
                />

                <FormSelect
                  name={STATE}
                  control={form.control}
                  label="State"
                  placeholder="Please enter"
                  error={errors[STATE]?.message}
                  isRequired={requiredFields.includes(STATE)}
                  options={Object.keys(stateList).map((val: string) => ({
                    label: val,
                    value: val,
                  }))}
                />
                <FormSelect
                  name={CITY}
                  control={form.control}
                  label="City"
                  placeholder="Please Select"
                  error={errors[CITY]?.message}
                  isRequired={requiredFields.includes(CITY)}
                  options={
                    form.watch(STATE)
                      ? (
                          stateList[
                            form.watch(STATE) as keyof typeof stateList
                          ] ?? []
                        ).map((val: string) => ({
                          label: val,
                          value: val,
                        }))
                      : []
                  }
                />

                <FormSelect
                  name={SKU}
                  control={form.control}
                  label="SKU"
                  placeholder="Enter SKU"
                  error={errors[SKU]?.message}
                  isRequired={requiredFields.includes(SKU)}
                  options={skuOptions}
                />

                <FormInput
                  name={NO_OF_WORK_STATIONS}
                  control={form.control}
                  label="No. of Work Stations"
                  placeholder="Enter number"
                  type="number"
                  error={errors[NO_OF_WORK_STATIONS]?.message}
                  isRequired={requiredFields.includes(NO_OF_WORK_STATIONS)}
                />

                <FormInput
                  name={NO_OF_FACULTY_DAYS}
                  control={form.control}
                  label="No. of Faculty Days"
                  placeholder="Enter number"
                  type="number"
                  error={errors[NO_OF_FACULTY_DAYS]?.message}
                  isRequired={requiredFields.includes(NO_OF_FACULTY_DAYS)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 absolute bottom-3 w-full bg-white pb-[25px] z-[3]">
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  type="button"
                  className="text-red-600"
                  onClick={onClear}
                >
                  Clear All
                </Button>
              </div>
              <Button type="submit">Apply Filter</Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default Filter;
